/*
 * The taskpane shell now matches the mock structure: a fixed top brand bar,
 * persistent thin navigation rail, and a wide panel workspace. Feature logic
 * stays inside the existing panels so auth, token, API, and Office flows remain
 * unchanged while the navigation model changes from tabs to rail buttons.
 */

import { useEffect } from "react";
import { MessageBar, MessageBarBody } from "@fluentui/react-components";
import { Header } from "./components/Header";
import { LoadingOverlay } from "./components/LoadingOverlay";
import { AccountPanel } from "./components/auth/AccountPanel";
import { RulesPanel } from "./components/rules/RulesPanel";
import { BatchPanel } from "./components/batch/BatchPanel";
import { SelectionPanel } from "./components/selection/SelectionPanel";
import { LivePanel } from "./components/live/LivePanel";
import { SuggestionsPanel } from "./components/suggestions/SuggestionsPanel";
import { FalsePositiveModal } from "./components/common/FalsePositiveModal";
import { NavIcon, type NavIconName } from "./components/icons/NavIcons";
import { liveController } from "./live/liveController";
import { useStore } from "./state/store";
import { hasTabFindings } from "./state/findings";
import { useTranslation } from "./i18n/useTranslation";
import { fetchSession, fetchCurrentSubscription, getRuleCatalog } from "./api/client";
import { buildDefaultPreferences, getConfusionRules } from "./rules/ruleLogic";
import type { TabId } from "./state/types";

interface NavItem {
  id: TabId;
  label: string;
  icon: NavIconName;
}

function useBootstrap(): void {
  const patch = useStore((s) => s.patch);
  const setLoading = useStore((s) => s.setLoading);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const session = await fetchSession();
        if (cancelled) return;
        if (session?.user) {
          const subscription = await fetchCurrentSubscription();
          if (cancelled) return;
          const catalog = await getRuleCatalog();
          if (cancelled) return;
          patch({
            sessionUser: session.user,
            subscription,
            authState: "logged-in",
            rules: catalog.rules,
            catalogPreferences: catalog.preferences,
            catalogSections: catalog.sections,
            dependencies: catalog.dependencies,
            karlCapability: catalog.karl,
            preferences: buildDefaultPreferences(catalog.preferences),
            selectedRuleIds: [],
            selectedConfusionRuleIds: getConfusionRules(catalog.rules).map((rule) => rule.id),
          });
        } else {
          patch({ authState: "logged-out" });
        }
      } catch {
        if (!cancelled) patch({ authState: "logged-out" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patch, setLoading]);
}

function useLiveRescanOnConfigChange(): void {
  const signature = useStore((s) =>
    JSON.stringify({
      ruleIds: s.selectedRuleIds,
      preferences: s.preferences,
      repetitionEnabled: s.liveRepetitionEnabled,
      window: s.liveRepetitionWindow,
      frequency: s.liveRepetitionFrequency,
    })
  );
  useEffect(() => {
    liveController.rescan();
  }, [signature]);
}

function navItems(t: ReturnType<typeof useTranslation>["t"]): NavItem[] {
  return [
    { id: "batch", label: t.tabBatch, icon: "apply" },
    { id: "live", label: t.tabLive, icon: "live" },
    { id: "suggestions", label: t.tabSuggestions, icon: "review" },
    { id: "selection", label: t.tabSelection, icon: "selection" },
    { id: "rules", label: t.tabRules, icon: "rules" },
    { id: "settings", label: t.tabSettings, icon: "account" },
  ];
}

function SideRail(): JSX.Element {
  const { t } = useTranslation();
  const activeTab = useStore((s) => s.activeTab);
  const setActiveTab = useStore((s) => s.setActiveTab);
  const items = navItems(t);
  const primaryItems = items.filter((item) => item.id !== "settings");
  const accountItem = items.find((item) => item.id === "settings")!;

  const renderItem = (item: NavItem) => (
    <button
      key={item.id}
      type="button"
      className={`side-rail__item${activeTab === item.id ? " side-rail__item--active" : ""}`}
      onClick={() => setActiveTab(item.id)}
      aria-current={activeTab === item.id ? "page" : undefined}
      aria-label={item.label}
    >
      <span className="side-rail__icon-wrap">
        <NavIcon name={item.icon} className="side-rail__icon" />
      </span>
      <span className="side-rail__label">{item.label}</span>
    </button>
  );

  return (
    <nav className="side-rail" aria-label="Add-in navigation">
      <div className="side-rail__top">{primaryItems.map(renderItem)}</div>
      <div className="side-rail__bottom">{renderItem(accountItem)}</div>
    </nav>
  );
}

function panelTitle(activeTab: TabId, t: ReturnType<typeof useTranslation>["t"]): string {
  switch (activeTab) {
    case "live":
      return t.tabLive;
    case "suggestions":
      return t.tabSuggestions;
    case "selection":
      return t.tabSelection;
    case "rules":
      return t.tabRules;
    case "settings":
      return t.tabSettings;
    case "batch":
    default:
      return t.tabBatch;
  }
}

function panelIcon(activeTab: TabId): NavIconName {
  switch (activeTab) {
    case "live":
      return "live";
    case "suggestions":
      return "review";
    case "selection":
      return "selection";
    case "rules":
      return "rules";
    case "settings":
      return "account";
    case "batch":
    default:
      return "apply";
  }
}

function FeaturePanelContent(): JSX.Element {
  const { t } = useTranslation();
  const activeTab = useStore((s) => s.activeTab);
  const authState = useStore((s) => s.authState);
  const isLoggedIn = authState === "logged-in";
  const rulesConfigured = useStore((s) => s.rulesConfigured);
  const selectedRuleIds = useStore((s) => s.selectedRuleIds);
  const hasFindings = useStore((s) => hasTabFindings(s, s.activeTab));

  if (activeTab === "settings") return <AccountPanel />;

  if (!isLoggedIn) {
    return (
      <MessageBar intent="warning" className="status-card status-card--warning">
        <MessageBarBody>{t.unauthWarning}</MessageBarBody>
      </MessageBar>
    );
  }

  const showRulesWarning =
    selectedRuleIds.length === 0 ||
    (activeTab !== "rules" && activeTab !== "selection" && !rulesConfigured);
  const showDependencyInfo =
    activeTab !== "rules" &&
    activeTab !== "selection" &&
    selectedRuleIds.length > 0 &&
    !hasFindings;

  return (
    <div className="panel-stack">
      {showRulesWarning ? (
        <MessageBar intent="warning" className="status-card status-card--warning">
          <MessageBarBody>{t.noRulesWarning}</MessageBarBody>
        </MessageBar>
      ) : null}
      {showDependencyInfo ? (
        <MessageBar intent="info" className="status-card">
          <MessageBarBody>{t.rulesDependencyInfo}</MessageBarBody>
        </MessageBar>
      ) : null}
      {(() => {
        switch (activeTab) {
          case "rules":
            return <RulesPanel />;
          case "live":
            return <LivePanel />;
          case "selection":
            return <SelectionPanel />;
          case "suggestions":
            return <SuggestionsPanel />;
          case "batch":
          default:
            return <BatchPanel />;
        }
      })()}
    </div>
  );
}

export function App(): JSX.Element {
  const { t } = useTranslation();
  useBootstrap();
  useLiveRescanOnConfigChange();

  const activeTab = useStore((s) => s.activeTab);
  const patch = useStore((s) => s.patch);
  const rulesConfigured = useStore((s) => s.rulesConfigured);

  useEffect(() => {
    if (activeTab === "rules" && !rulesConfigured) {
      patch({ rulesConfigured: true });
    }
  }, [activeTab, rulesConfigured, patch]);

  return (
    <div className="app-shell">
      <Header />
      <div className="app-shell__body">
        <SideRail />
        <main className="content-shell">
          <header className="content-shell__header">
            <NavIcon name={panelIcon(activeTab)} className="content-shell__icon" />
            <h1 className="content-shell__title">{panelTitle(activeTab, t)}</h1>
          </header>
          <section className="content-shell__body" aria-label={panelTitle(activeTab, t)}>
            <FeaturePanelContent />
          </section>
        </main>
      </div>
      <LoadingOverlay />
      <FalsePositiveModal />
    </div>
  );
}
